import { useDispatch } from 'react-redux';
import { Url } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { BiData, SubmitButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { Props as BaseButtonProps } from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink/PlainButtonOrLink';
import { useRestrictToLoggedInAndVerified } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedInAndVerified';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';

export type UseSubmitButtonPropsProps = {
  /** deviation tags that will be pre-filled in the submit form */
  tags?: string | string[];
  /** should we use eclipse submit regardless of whether eclipse_submit flag is on? */
  forceEclipseSubmit?: boolean;
  biSectionname?: string;
};

export type SubmitButtonProps = Pick<
  BaseButtonProps,
  'onClick' | 'href' | 'biData'
>;

/** Return the href, biData, and onClick required for a submit button (verification handled) */
export default function useSubmitButtonProps(
  {
    tags,
    forceEclipseSubmit,
    biSectionname = 'general',
  } = {} as UseSubmitButtonPropsProps
): SubmitButtonProps {
  const eclipse_submit = useFlag('eclipse_submit');
  const submitToTag = useFlag('submit_to_tag') && tags;
  const newSubmitEnabled = eclipse_submit || forceEclipseSubmit;

  const dispatch = useDispatch();
  const openSubmitModal = () =>
    dispatch(pushModal(ModalType.DEVIATION_SUBMIT, {}));

  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
  const onClick = restrictToLoggedInAndVerified(
    ReferralType.SUBMIT,
    'submit',
    !submitToTag && newSubmitEnabled ? openSubmitModal : undefined
  );

  // Note: Under `submit_to_tag` feature we open new submit, except we load the standalone version instead of using a modal
  let submitHref = submitToTag
    ? Url.buildFromPath('/_deviation_submit')
    : Url.buildFromPath('/submit');
  if (tags) {
    const tagsAsParam = Array.isArray(tags) ? tags.join(',') : tags;
    submitHref = `${submitHref}/?tags=${tagsAsParam}`;
  }
  const href = submitToTag || !newSubmitEnabled ? submitHref : undefined;

  const biData = BiData<SubmitButtonClickBiEvent>({
    evid: 704,
    sectionname: biSectionname,
  });

  return {
    onClick,
    href,
    biData,
  };
}
